// Subscription.js
import { useContext, useEffect, useState } from 'react';
import { FaDollarSign, FaStar } from 'react-icons/fa';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-toastify';

import CheckoutForm from './CheckoutForm';
import SubContext from '../../contexts/SubContext';
import { deleteWithToken, getLocalStorage, getWithToken, patchWithToken, postWithToken } from '../../API/Api';
import { AnimatedPrice, DiscountBadge, getPlanAction, planIcons } from '../../API/Constant';
import CHECK_CIRCLEICON from '../../assets/icons/ComniqaiIcon-V3.0/check-circleicon.svg';
import CHECK_SKYCIRCLEICON from '../../assets/icons/ComniqaiIcon-V3.0/check-orangecircleicon.svg';
import ButtonLoader from '../../components/ButtonLoader';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// --- Billing Toggle Component ---
const BillingToggle = ({ billingCycle, setBillingCycle }) => (
  <div className='billing-toggle d-flex justify-content-center mb-5'>
    <div className='billing-toggle-wrapper p-2'>
      <button className={`billing-btn outfit-medium ${billingCycle === 'monthly' ? 'active' : ''}`} onClick={() => setBillingCycle('monthly')}>
        Monthly
      </button>
      <button className={`billing-btn outfit-medium position-relative ${billingCycle === 'yearly' ? 'active' : ''}`} onClick={() => setBillingCycle('yearly')}>
        Yearly
        <span className='billing-badge-discount outfit-semibold'>10% Off</span>
      </button>
    </div>
  </div>
);

// --- Subscription Card Component ---
const SubscriptionCard = ({
  products,
  subscriptionDetails,
  checkoutSubscriptionData,
  setUnsubsPopup,
  submitPlanInterest,
  loadingPriceId,
  apiTokenData,
  submitInterestLoading,
  billingCycle,
}) => {
  const isActive = products?.id === subscriptionDetails?.user?.subscription?.id;
  const isLoading = products?.price > 0 && loadingPriceId === products?.price_id;
  const isPendingCancellation = subscriptionDetails?.user?.is_pending_cancellation;
  const isPendingDowngrade = subscriptionDetails?.user?.is_pending_downgrade && subscriptionDetails?.user?.downgrade_subscription === products?.id;

  return (
    <div
      className={`card Pro subscription-box ${
        products?.price === 0 && !subscriptionDetails?.user?.subscription?.id
          ? 'active-subscription-border'
          : isActive
          ? 'active-subscription-border'
          : 'inactive-subscription-border'
      }`}
    >
      {products?.is_best_seller && (
        <div className='subscription-best'>
          <p className='d-flex align-items-center gap-1 font14 outfit-semibold m-0'>
            <FaStar /> Most Subscribed
          </p>
        </div>
      )}

      <div className='subscription-card-body p-0 d-flex flex-column'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className='subscription-icon-box d-flex align-items-center justify-content-center'
            style={{
              background: isActive ? '#CFFAFE' : '#F3F4F6',
            }}
          >
            <div className='subscription-icon d-flex align-items-center justify-content-center'>{planIcons[products?.name]}</div>
          </div>
          <h4 className='subscription-card-title outfit-bold'>{products?.name}</h4>
        </div>

        <div className='subscription-cost mt-3'>
          {products?.name?.toLowerCase() === 'enterprise' ? (
            <h1 className='subscription-card-subtitle outfit-semibold'>Let's Talk</h1>
          ) : (
            <>
              <div className='d-flex align-items-center gap-2'>
                <AnimatedPrice price={products?.description} billing={billingCycle} planName={products.name} />
                {billingCycle === 'yearly' && <DiscountBadge discount={10} billing={billingCycle} planName={products.name} />}
              </div>
              <p className='price-subtext outfit-regular m-0'>
                {products?.price === 0 ? 'per month' : billingCycle === 'monthly' ? 'per month' : `per month, $${products?.price} billed annually`}
              </p>
            </>
          )}
        </div>

        <div className='subscription-button my-4'>
          {products?.name?.toLowerCase() === 'enterprise' ? (
            <button
              className='btn font-weight-bold font16 outfit-regular'
              onClick={() => submitPlanInterest(apiTokenData)}
              disabled={submitInterestLoading || subscriptionDetails?.user?.is_enterprise_request_send}
            >
              {submitInterestLoading ? (
                <div className='d-flex align-items-center justify-content-center gap-2'>
                  <ButtonLoader />
                  <span className='outfit-regular'>Submitting...</span>
                </div>
              ) : (
                <span className='outfit-regular'>{subscriptionDetails?.user?.is_enterprise_request_send ? 'Already Applied' : 'Contact Us'}</span>
              )}
            </button>
          ) : (
            <button
              className='btn font-weight-bold font16 outfit-regular'
              disabled={products?.price === 0 || isActive || isLoading || getPlanAction(products, subscriptionDetails) === 'Get Started' || isPendingDowngrade}
              style={{
                background: isActive ? '#dbdfe7' : 'linear-gradient(to right, #b94feb, #f4543a)',
                color: isActive ? '#000000' : '#ffffff',
              }}
              onClick={() => !isActive && checkoutSubscriptionData(products?.price_id, products)}
            >
              {isLoading ? (
                <div className='d-flex align-items-center justify-content-center gap-2'>
                  <ButtonLoader />
                  <span className='outfit-regular'>Upgrading...</span>
                </div>
              ) : isPendingDowngrade ? (
                <span className='outfit-regular'>Downgrade Pending</span> // new logic
              ) : (
                getPlanAction(products, subscriptionDetails)
              )}
            </button>
          )}
        </div>

        <div className='subscription-description'>
          <ul>
            {products?.features.map((item, index) => {
              const isEverything = item?.name?.startsWith('Everything in');
              return (
                <li key={index} className={`d-flex align-items-center gap-2 ${isEverything ? 'outfit-semibold' : 'outfit-regular'}`}>
                  <img src={isActive ? CHECK_SKYCIRCLEICON : CHECK_CIRCLEICON} alt='' />
                  {item?.name}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {isActive && !isPendingCancellation && (
        <div className='subscription-button mt-auto'>
          <button className='btn font-weight-bold font16 outfit-regular text-white' disabled={products?.price === 0} onClick={() => setUnsubsPopup(true)}>
            Unsubscribe
          </button>
        </div>
      )}
    </div>
  );
};

// --- Subscription Grid Component ---
const SubscriptionGrid = ({
  subscriptionDetails,
  checkoutSubscriptionData,
  setUnsubsPopup,
  submitPlanInterest,
  loadingPriceId,
  apiTokenData,
  submitInterestLoading,
  billingCycle,
}) => {
  return (
    <div className='subscription-grid'>
      {subscriptionDetails[billingCycle]?.map((products, index) => (
        <SubscriptionCard
          key={index}
          products={products}
          subscriptionDetails={subscriptionDetails}
          checkoutSubscriptionData={checkoutSubscriptionData}
          setUnsubsPopup={setUnsubsPopup}
          submitPlanInterest={submitPlanInterest}
          loadingPriceId={loadingPriceId}
          apiTokenData={apiTokenData}
          submitInterestLoading={submitInterestLoading}
          billingCycle={billingCycle}
        />
      ))}
    </div>
  );
};

// --- Plan Action Popup ---
const PlanActionPopup = ({ productsData, subscriptionDetails, handleUpgradeData, setIsPlanActionPopupOpen, upgradeLoading }) => {
  const action = getPlanAction(productsData, subscriptionDetails);
  const loadingText = action.endsWith('e') ? `${action.slice(0, -1)}ing...` : `${action}ing...`;
  return (
    <div className='custom-popup'>
      <div className='popup-content' style={{ maxWidth: '460px' }}>
        <div className='popup-body'>
          <h4 className='font16 outfit-regular'>{`Are you sure you want to ${getPlanAction(productsData, subscriptionDetails).toLowerCase()} to the 
           ${productsData?.name} plan?`}</h4>
        </div>
        <hr />
        <div className='popup-footer d-flex align-items-center gap-2'>
          <button className='btn-modal-save outfit-medium' onClick={() => handleUpgradeData(productsData)} disabled={upgradeLoading}>
            {upgradeLoading ? (
              <div className='d-flex align-items-center justify-content-center gap-2'>
                <ButtonLoader />
                <span>{loadingText}</span>
              </div>
            ) : (
              getPlanAction(productsData, subscriptionDetails)
            )}
          </button>
          <button className='btn-modal-cancel outfit-medium' onClick={() => setIsPlanActionPopupOpen(false)} disabled={upgradeLoading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Unsubscribe Popup ---
const UnsubscribePopup = ({ cancelSubscriptionData, setUnsubsPopup, apiTokenData, unsubscribeLoading }) => (
  <div className='custom-popup'>
    <div className='popup-content' style={{ maxWidth: '450px' }}>
      <div className='popup-head'>
        <p className='font16 outfit-regular mb-0'>Your current plan will be unsubscribed now?</p>
      </div>
      <hr />
      <div className='popup-footer d-flex align-items-center gap-2'>
        <button className='btn-modal-save outfit-medium' onClick={() => cancelSubscriptionData(apiTokenData)} disabled={unsubscribeLoading}>
          {unsubscribeLoading ? (
            <div className='d-flex align-items-center justify-content-center gap-2'>
              <ButtonLoader />
              <span>Unsubscribing..</span>
            </div>
          ) : (
            'Yes'
          )}
        </button>
        <button className='btn-modal-cancel outfit-medium' onClick={() => setUnsubsPopup(false)}>
          No
        </button>
      </div>
    </div>
  </div>
);

// --- Add-on Toggle Popup Component ---
const AddOnTogglePopup = ({ addon, isEnabled, onConfirm, onCancel, isLoading }) => {
  const action = isEnabled ? 'disable' : 'enable';
  const actionCapitalized = action.charAt(0).toUpperCase() + action.slice(1);

  return (
    <div className='custom-popup'>
      <div className='popup-content' style={{ maxWidth: '500px' }}>
        <div className='popup-head'>
          <h4 className='font18 outfit-semibold mb-2'>{actionCapitalized} Add-on</h4>
        </div>

        <div className='popup-body'>
          <p className='font16 outfit-regular mb-2'>
            Are you sure you want to <strong>{action}</strong> the <strong>{addon.name}</strong> add-on?
          </p>

          <div className='addon-popup-details mt-3 p-3' style={{ background: '#f9fafb', borderRadius: '8px' }}>
            <p className='font14 outfit-medium mb-1'>{addon.name}</p>
            <p className='font14 outfit-regular mb-1' style={{ color: '#6b7280' }}>
              ${addon.price} {addon.interval === 'month' && '/ month'}
            </p>

            {addon.description && (
              <p className='font13 outfit-regular mb-0' style={{ color: '#9ca3af' }}>
                {addon.description}
              </p>
            )}
          </div>
        </div>

        <hr />

        <div className='popup-footer d-flex align-items-center gap-2'>
          <button className='btn-modal-save outfit-medium' onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <div className='d-flex align-items-center justify-content-center gap-2'>
                <ButtonLoader />
                <span>{actionCapitalized === 'Enable' ? 'Enabling...' : 'Disabling...'}</span>
              </div>
            ) : (
              `Yes, ${actionCapitalized}`
            )}
          </button>
          <button className='btn-modal-cancel outfit-medium' onClick={onCancel} disabled={isLoading}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Add-ons Component ---
const AddOnsSection = ({ subscriptionDetails, apiTokenData }) => {
  const addOnsList = subscriptionDetails?.addons || [];

  // Store active state per addon ID
  const [addOns, setAddOns] = useState({});
  const [popupState, setPopupState] = useState({
    isOpen: false,
    addon: null,
    isEnabled: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize addon state from backend
  useEffect(() => {
    if (addOnsList.length > 0) {
      const initialState = {};
      addOnsList.forEach((addon) => {
        initialState[addon.id] = addon.is_enabled_for_user;
      });
      setAddOns(initialState);
    }
  }, [addOnsList]);

  const handleToggleClick = (addon) => {
    const currentState = addOns[addon.id];
    setPopupState({
      isOpen: true,
      addon,
      isEnabled: currentState,
    });
  };

  const handleConfirmToggle = async () => {
    const { addon, isEnabled } = popupState;
    setIsLoading(true);

    try {
      if (!isEnabled) {
        const payload = { price_id: addon.price_id };
        const response = await postWithToken('/addons/buy/', apiTokenData, payload);

        if (response?.status === 200 || response?.success) {
          window.location.href = response.checkout_url;
          setAddOns((prev) => ({
            ...prev,
            [addon.id]: !isEnabled,
          }));
        } else {
          toast.error(response?.message || 'Failed to enable add-on. Please try again.');
        }
      } else {
        const disableAddon = subscriptionDetails?.user?.addons.find((a) => a?.subscription_addon?.id === addon?.id);
        const endpoint = `/addons_cancel/${disableAddon?.subs_id}/`;
        const response = await deleteWithToken(endpoint, apiTokenData, {});

        if (response?.status === 200 || response?.success) {
          toast.success(response?.message || `${addon.name} disabled successfully!`);
          setAddOns((prev) => ({
            ...prev,
            [addon.id]: !isEnabled,
          }));
        } else {
          toast.error(response?.message || 'Failed to disable add-on. Please try again.');
        }
      }

      setPopupState({ isOpen: false, addon: null, isEnabled: false });
    } catch (err) {
      console.error(err);
      toast.error('Failed to update add-on. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelToggle = () => {
    setPopupState({ isOpen: false, addon: null, isEnabled: false });
  };

  return (
    <>
      <div className='add-ons-section'>
        <div className='add-ons-partation'></div>
        <h3 className='add-ons-title outfit-bold mb-4'>Add-ons</h3>

        <div className='add-ons-grid'>
          {addOnsList?.map((addon) => (
            <div key={addon.id} className='add-on-card'>
              <div className='add-on-content'>
                <div className='add-on-header d-flex justify-content-between align-items-center'>
                  <h4 className='add-on-title outfit-semibold'>{addon.name}</h4>

                  <div className='form-check form-switch'>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      checked={!!addOns[addon.id]}
                      onChange={() => handleToggleClick(addon)}
                      disabled={addon.interval === 'one_time' && addon.is_enabled_for_user}
                    />
                  </div>
                </div>

                <p className='add-on-price outfit-medium'>
                  ${addon.price} {addon.interval === 'month' && '/ month'}
                </p>
                {addon.description && <p className='add-on-description outfit-regular'>{addon.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {popupState.isOpen && (
        <AddOnTogglePopup addon={popupState.addon} isEnabled={popupState.isEnabled} onConfirm={handleConfirmToggle} onCancel={handleCancelToggle} isLoading={isLoading} />
      )}
    </>
  );
};

// --- Main Subscription Component ---
const Subscription = () => {
  const { refreshSubscription } = useContext(SubContext);
  const [subscriptionTab, setSubscriptionTab] = useState('1');
  const [subscriptionDetails, setSubscriptionDetails] = useState([]);
  const [apiTokenData, setApiTokenData] = useState('');
  const [isPlanActionPopupOpen, setIsPlanActionPopupOpen] = useState(false);
  const [unsubsPopup, setUnsubsPopup] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({
    subscriptionId: '',
    clientSecret: '',
  });
  const [productsData, setProductsData] = useState();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [loadingPriceId, setLoadingPriceId] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [submitInterestLoading, setSubmitInterestLoading] = useState(false);

  useEffect(() => {
    const token = getLocalStorage('apiToken');
    if (token) {
      const parsedToken = JSON.parse(token);
      setApiTokenData(parsedToken);
      getSubscriptionDetails(parsedToken);
    }
  }, []);

  const getSubscriptionDetails = async (token) => {
    try {
      setLoading(true);
      const response = await getWithToken('list_subscription', token);
      if (response?.data) setSubscriptionDetails(response?.data);
      else console.error('No products found.');
    } catch (error) {
      console.error('Error fetching subscription details:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkoutSubscriptionData = async (priceId, products) => {
    setProductsData(products);
    if (!priceId) return;
    try {
      setLoadingPriceId(priceId);
      if (!subscriptionDetails?.user?.subs_id) {
        const response = await postWithToken('create_subscription', apiTokenData, { price_id: priceId });
        if (response?.status === 200) {
          // setSubscriptionTab('3');
          // setSubscriptionData({ subscriptionId: response?.subscriptionId, clientSecret: response?.clientSecret });
          window.location.href = response.checkout_url;
        } else toast.error(response.message);
      } else setIsPlanActionPopupOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPriceId(null);
    }
  };

  const handleUpgradeData = async (productsData) => {
    try {
      setUpgradeLoading(true);
      const response = await patchWithToken('update_subscription', apiTokenData, { price_id: productsData?.price_id });
      if (response?.status === 200) {
        setIsPlanActionPopupOpen(false);
        await refreshSubscription();
        window.location.reload();
        toast.error(response.message);
      } else toast.error(response.message);
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
    } finally {
      setUpgradeLoading(false);
    }
  };

  const cancelSubscriptionData = async (apiTokenData) => {
    try {
      setUnsubscribeLoading(true); // start loader
      const response = await deleteWithToken('cancel_subscription', apiTokenData);
      if (response.status === 200) {
        await refreshSubscription();
        window.location.reload();
        toast.success(response?.message);
      } else {
        toast.error(response?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error('Something went wrong');
      console.error(error);
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  const submitPlanInterest = async (token) => {
    try {
      setSubmitInterestLoading(true);
      const response = await getWithToken('enterprise_plan_interest', token);
      if (response?.status === 200) {
        getSubscriptionDetails(apiTokenData);
        toast.success(response.message || 'Request submitted successfully.');
      } else {
        toast.info('No products found.');
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      toast.error('Failed to submit your interest. Please try again.');
    } finally {
      setSubmitInterestLoading(false);
    }
  };

  const appearance = {
    theme: 'flat',
    variables: {
      colorPrimary: '#eff6ff',
      colorBackground: '#f8fafc',
      colorText: '#374151',
      colorDanger: '#df1b41',
      fontFamily: 'Ideal Sans, system-ui, sans-serif',
      fontSizeBase: '14px',
      borderRadius: '12px',
      height: '45px',
    },
    rules: {
      '.Input, .Block': { border: '1.5px solid #d1d5db' },
      '.Label': { marginBottom: '10px' },
      '.Tab:hover, .Tab:focus, .Input:hover, .Input:focus': { boxShadow: '0 0 0 2px #fff, 0 0 0 4px #3c83f6' },
    },
  };

  return (
    <div className='intelichat-conatiner-right'>
      <div className='row m-0 ps-md-5 ps-sm-4 ps-4 pe-md-5 pe-sm-4 pe-4 mb-3'>
        <div className='col-12 p-0'>
          <div className='subscription-section'>
            {subscriptionTab === '1' && (
              <>
                <div className='subscription-header text-center pb-3 position-relative'>
                  <h2 className='subscription-header__title outfit-bold'>Choose Your Perfect Plan</h2>
                  <p className='subscription-header__subtitle outfit- mb-0'>Scale your customer engagement with the perfect plan to meet your needs</p>
                </div>

                <BillingToggle billingCycle={billingCycle} setBillingCycle={setBillingCycle} />

                {loading ? (
                  <div className='d-flex justify-content-center align-items-center p-3'>
                    <div className='triple-bounce'>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  </div>
                ) : (
                  <div className='subscription-content row mx-0'>
                    <SubscriptionGrid
                      subscriptionDetails={subscriptionDetails}
                      checkoutSubscriptionData={checkoutSubscriptionData}
                      setUnsubsPopup={setUnsubsPopup}
                      submitPlanInterest={submitPlanInterest}
                      loadingPriceId={loadingPriceId}
                      apiTokenData={apiTokenData}
                      submitInterestLoading={submitInterestLoading}
                      billingCycle={billingCycle}
                    />

                    {subscriptionDetails?.addons?.length > 0 && <AddOnsSection subscriptionDetails={subscriptionDetails} apiTokenData={apiTokenData} />}
                    <div className='text-center mt-3'>
                      <p className='terms-text outfit-regular'>
                        By continuing, you agree to our{' '}
                        <a href='https://www.comniqai.com/terms-of-service' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
                          Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href='https://www.comniqai.com/privacy-policy' target='_blank' rel='noopener noreferrer' className='terms-link outfit-medium'>
                          Privacy Policy
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {subscriptionTab === '3' && subscriptionData?.clientSecret && (
              <Elements options={{ clientSecret: subscriptionData?.clientSecret, appearance }} stripe={stripePromise}>
                <CheckoutForm setSubscriptionTab={setSubscriptionTab} productsData={productsData} />
              </Elements>
            )}
          </div>
        </div>
      </div>

      {isPlanActionPopupOpen && (
        <PlanActionPopup
          productsData={productsData}
          subscriptionDetails={subscriptionDetails}
          handleUpgradeData={handleUpgradeData}
          setIsPlanActionPopupOpen={setIsPlanActionPopupOpen}
          upgradeLoading={upgradeLoading}
        />
      )}

      {unsubsPopup && (
        <UnsubscribePopup cancelSubscriptionData={cancelSubscriptionData} setUnsubsPopup={setUnsubsPopup} apiTokenData={apiTokenData} unsubscribeLoading={unsubscribeLoading} />
      )}
    </div>
  );
};

export default Subscription;
